import { supabase } from '../lib/supabase';

/**
 * Alert Notification Service
 * Manages security alerts and compliance notifications
 */

/**
 * Create a security alert
 * @param {Object} alertData - Alert data
 * @returns {Object} Result with data or error
 */
export const createSecurityAlert = async (alertData) => {
  try {
    const {
      alertType,
      priority = 'medium',
      title,
      description,
      entityType = null,
      entityId = null,
      entityName = null,
      dealershipId = null,
      applicationId = null,
      auditLogId = null,
      triggeredByUserEmail = null,
      affectedData = null,
      recommendedAction = null,
      metadata = null
    } = alertData;

    const { data, error } = await supabase
      ?.from('security_alerts')
      ?.insert({
        alert_type: alertType,
        alert_status: 'new',
        priority: priority,
        title: title,
        description: description,
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        dealership_id: dealershipId,
        application_id: applicationId,
        audit_log_id: auditLogId,
        triggered_by_user_email: triggeredByUserEmail,
        affected_data: affectedData,
        recommended_action: recommendedAction,
        metadata: metadata
      })
      ?.select()
      ?.single();

    if (error) {
      console.error('Error creating security alert:', error);
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Exception in createSecurityAlert:', error);
    return { error };
  }
};

/**
 * Fetch security alerts with filters
 * @param {Object} filters - Filter options
 * @returns {Array} Security alerts
 */
export const fetchSecurityAlerts = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('security_alerts')
      ?.select(`
        *,
        dealerships (id, name),
        applications (id, customer_name, customer_email),
        audit_logs (id, event_type, action_description)
      `)
      ?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.alertType) {
      query = query?.eq('alert_type', filters?.alertType);
    }
    if (filters?.status) {
      query = query?.eq('alert_status', filters?.status);
    }
    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }
    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }
    if (filters?.dealershipId) {
      query = query?.eq('dealership_id', filters?.dealershipId);
    }
    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching security alerts:', error);
      return { data: [], error };
    }

    // Convert to camelCase
    const formattedAlerts = data?.map(alert => ({
      id: alert?.id,
      alertType: alert?.alert_type,
      alertStatus: alert?.alert_status,
      priority: alert?.priority,
      title: alert?.title,
      description: alert?.description,
      entityType: alert?.entity_type,
      entityId: alert?.entity_id,
      entityName: alert?.entity_name,
      dealershipId: alert?.dealership_id,
      dealershipName: alert?.dealerships?.name || null,
      applicationId: alert?.application_id,
      applicationCustomerName: alert?.applications?.customer_name || null,
      auditLogId: alert?.audit_log_id,
      triggeredByUserEmail: alert?.triggered_by_user_email,
      affectedData: alert?.affected_data,
      recommendedAction: alert?.recommended_action,
      metadata: alert?.metadata,
      acknowledgedBy: alert?.acknowledged_by,
      acknowledgedAt: alert?.acknowledged_at,
      resolvedBy: alert?.resolved_by,
      resolvedAt: alert?.resolved_at,
      resolutionNotes: alert?.resolution_notes,
      createdAt: alert?.created_at,
      updatedAt: alert?.updated_at
    })) || [];

    return { data: formattedAlerts, error: null };
  } catch (error) {
    console.error('Exception in fetchSecurityAlerts:', error);
    return { data: [], error };
  }
};

/**
 * Update alert status
 * @param {string} alertId - Alert ID
 * @param {Object} updateData - Update data
 * @returns {Object} Result with data or error
 */
export const updateAlertStatus = async (alertId, updateData) => {
  try {
    const { status, userEmail, resolutionNotes = null } = updateData;

    const updates = {
      alert_status: status
    };

    if (status === 'acknowledged') {
      updates.acknowledged_by = userEmail;
      updates.acknowledged_at = new Date()?.toISOString();
    } else if (status === 'resolved') {
      updates.resolved_by = userEmail;
      updates.resolved_at = new Date()?.toISOString();
      if (resolutionNotes) {
        updates.resolution_notes = resolutionNotes;
      }
    }

    const { data, error } = await supabase
      ?.from('security_alerts')
      ?.update(updates)
      ?.eq('id', alertId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating alert status:', error);
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Exception in updateAlertStatus:', error);
    return { error };
  }
};

/**
 * Get alert statistics
 * @param {Object} filters - Filter options
 * @returns {Object} Statistics
 */
export const getAlertStats = async (filters = {}) => {
  try {
    const { data: alerts, error } = await fetchSecurityAlerts(filters);

    if (error) {
      return { error };
    }

    const stats = {
      total: alerts?.length || 0,
      byStatus: {
        new: 0,
        acknowledged: 0,
        investigating: 0,
        resolved: 0,
        dismissed: 0
      },
      byPriority: {
        low: 0,
        medium: 0,
        high: 0,
        critical: 0
      },
      byType: {},
      recentCritical: alerts?.filter(alert => alert?.priority === 'critical' && alert?.alertStatus === 'new')?.slice(0, 5) || [],
      unresolvedCount: alerts?.filter(alert => alert?.alertStatus !== 'resolved' && alert?.alertStatus !== 'dismissed')?.length || 0
    };

    alerts?.forEach(alert => {
      // Count by status
      stats.byStatus[alert?.alertStatus] = (stats?.byStatus?.[alert?.alertStatus] || 0) + 1;
      
      // Count by priority
      stats.byPriority[alert?.priority] = (stats?.byPriority?.[alert?.priority] || 0) + 1;
      
      // Count by type
      stats.byType[alert?.alertType] = (stats?.byType?.[alert?.alertType] || 0) + 1;
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('Exception in getAlertStats:', error);
    return { error };
  }
};

/**
 * Subscribe to real-time alert updates
 * @param {Function} callback - Callback function for new alerts
 * @returns {Object} Subscription object
 */
export const subscribeToAlerts = (callback) => {
  const subscription = supabase
    ?.channel('security_alerts_channel')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'security_alerts'
      },
      (payload) => {
        console.log('New security alert:', payload?.new);
        callback(payload?.new);
      }
    )
    ?.subscribe();

  return subscription;
};

/**
 * Unsubscribe from alert updates
 * @param {Object} subscription - Subscription object
 */
export const unsubscribeFromAlerts = async (subscription) => {
  if (subscription) {
    await supabase?.removeChannel(subscription);
  }
};

/**
 * Check for duplicate license plate and create alert if found
 * @param {string} licensePlate - License plate to check
 * @param {string} applicationId - Current application ID
 * @returns {Object} Result with duplicate info
 */
export const checkDuplicateLicensePlate = async (licensePlate, applicationId) => {
  try {
    const { data: existingApps, error } = await supabase
      ?.from('applications')
      ?.select('id, customer_name, customer_email, dealerships(name)')
      ?.eq('customer_license_plate', licensePlate)
      ?.neq('id', applicationId);

    if (error) {
      console.error('Error checking duplicate license plate:', error);
      return { hasDuplicate: false, error };
    }

    if (existingApps && existingApps?.length > 0) {
      return {
        hasDuplicate: true,
        duplicateCount: existingApps?.length,
        existingApplications: existingApps,
        error: null
      };
    }

    return { hasDuplicate: false, duplicateCount: 0, existingApplications: [], error: null };
  } catch (error) {
    console.error('Exception in checkDuplicateLicensePlate:', error);
    return { hasDuplicate: false, error };
  }
};

/**
 * Bulk acknowledge alerts
 * @param {Array} alertIds - Array of alert IDs
 * @param {string} userEmail - User email
 * @returns {Object} Result
 */
export const bulkAcknowledgeAlerts = async (alertIds, userEmail) => {
  try {
    const { data, error } = await supabase
      ?.from('security_alerts')
      ?.update({
        alert_status: 'acknowledged',
        acknowledged_by: userEmail,
        acknowledged_at: new Date()?.toISOString()
      })
      ?.in('id', alertIds)
      ?.select();

    if (error) {
      console.error('Error bulk acknowledging alerts:', error);
      return { error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in bulkAcknowledgeAlerts:', error);
    return { error };
  }
};